/**
 * Admin → Template Versions (Vahid 2026-07-21).
 *
 * Visual tracking of per-report PDF TEMPLATE versions ("v.YY.MM" printed in
 * each report's footer — distinct from the app release):
 *
 *  - Summary grid: every report's current version, when it last changed and
 *    WHO applied the change (person, or Claude Code with the exact model).
 *  - Timeline: the full append-only change history (newest first), filterable
 *    by report, each entry expandable to its change bullets + authorship
 *    (applied-by, requested-by, session context).
 *
 * Data: GET /api/report-templates/versions-log — served from the git-tracked
 * backend/src/report/templateChangelog.ts (the single source of truth; the
 * footer stamp derives from the same file, so this page can never drift from
 * what the PDFs print).
 */

import {
  Alert, Box, Chip, CircularProgress, Collapse, IconButton, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  ExpandLess, ExpandMore, HistoryOutlined, PersonOutlined, SmartToyOutlined,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import {
  fetchTemplateVersionLog,
  type TemplateChangeAuthor,
  type TemplateChangeEntry,
  type TemplateVersionLog,
} from '../../../services/templateVersionLogService';

/** Applied-by badge: robot chip for AI authors, person chip for humans —
 *  full detail (tool/model or email) in the tooltip. */
const AuthorChip = ({ author, size = 'small' }: { author: TemplateChangeAuthor; size?: 'small' | 'medium' }) => {
  const chip = (
    <Chip
      size={size}
      icon={author.kind === 'ai' ? <SmartToyOutlined /> : <PersonOutlined />}
      label={author.name}
      color={author.kind === 'ai' ? 'secondary' : 'default'}
      variant="outlined"
      sx={{ fontWeight: 500 }}
    />
  );
  return author.detail ? <Tooltip title={author.detail}>{chip}</Tooltip> : chip;
};

/** One timeline entry card (collapsible change bullets). */
const HistoryEntry = ({ entry, defaultOpen }: { entry: TemplateChangeEntry; defaultOpen: boolean }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip label={entry.version} color="primary" size="small" sx={{ fontWeight: 700 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{entry.reportName}</Typography>
        <Typography variant="caption" color="text.secondary">{entry.date}</Typography>
        <AuthorChip author={entry.appliedBy} />
        {entry.requestedBy && (
          <Tooltip title={`Requested by ${entry.requestedBy}`}>
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260 }} noWrap>
              for {entry.requestedBy.replace(/\s*\(.*\)$/, '')}
            </Typography>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={() => setOpen((o) => !o)} aria-label={open ? 'Collapse' : 'Expand'}>
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Stack>
      <Collapse in={open}>
        {entry.context && (
          <Typography variant="caption" sx={{
            display: 'block', mt: 1, px: 1.25, py: 0.75, borderRadius: 1,
            backgroundColor: alpha(theme.palette.info.main, 0.06), color: 'text.secondary',
          }}>
            {entry.context}
          </Typography>
        )}
        <Box component="ul" sx={{ m: 0, mt: 1, pl: 3 }}>
          {entry.changes.map((c, i) => (
            <Typography key={i} component="li" variant="body2" sx={{ py: 0.2 }}>{c}</Typography>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

export const TemplateVersionsPage = () => {
  const theme = useTheme();
  const [log, setLog] = useState<TemplateVersionLog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    fetchTemplateVersionLog()
      .then(setLog)
      .catch(() => setError('Failed to load the template version log.'));
  }, []);

  const history = useMemo(
    () => (log ? log.history.filter((e) => filter === 'all' || e.reportKey === filter) : []),
    [log, filter],
  );
  /** Timeline groups: entries under their date (newest date first, backend order). */
  const groups = useMemo(() => {
    const byDate: Array<{ date: string; entries: TemplateChangeEntry[] }> = [];
    for (const e of history) {
      const g = byDate[byDate.length - 1];
      if (g && g.date === e.date) g.entries.push(e);
      else byDate.push({ date: e.date, entries: [e] });
    }
    return byDate;
  }, [history]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!log) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <HistoryOutlined color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Template Versions</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Each PDF report footer prints its own template version (v.YY.MM = year.month of the last
        template edit). The log below is append-only and git-tracked — every change records what
        changed and who applied it.
      </Typography>

      {/* ── Current versions grid ─────────────────────────────────────────── */}
      <Box sx={{
        display: 'grid', gap: 1.5, mb: 3,
        gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
      }}>
        {log.reports.map((r) => (
          <Paper
            key={r.key}
            variant="outlined"
            onClick={() => setFilter(filter === r.key ? 'all' : r.key)}
            sx={{
              p: 1.5, borderRadius: 2, cursor: 'pointer',
              borderColor: filter === r.key ? theme.palette.primary.main : undefined,
              backgroundColor: filter === r.key ? alpha(theme.palette.primary.main, 0.05) : undefined,
              '&:hover': { borderColor: theme.palette.primary.main },
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{r.name}</Typography>
              <Chip label={r.version ?? '—'} color="primary" size="small" sx={{ fontWeight: 700 }} />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              {r.lastAppliedBy && <AuthorChip author={r.lastAppliedBy} />}
              <Typography variant="caption" color="text.secondary">{r.lastChanged ?? ''}</Typography>
            </Stack>
          </Paper>
        ))}
      </Box>

      {/* ── Filter state + timeline ───────────────────────────────────────── */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Change history</Typography>
        {filter !== 'all' && (
          <Chip
            size="small"
            label={`${log.reports.find((r) => r.key === filter)?.name ?? filter} only`}
            onDelete={() => setFilter('all')}
          />
        )}
      </Stack>

      {groups.length === 0 && (
        <Typography variant="body2" color="text.secondary">No entries.</Typography>
      )}
      {groups.map((g) => (
        <Box key={g.date} sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {/* Date rail: dot + connector line, app-changelog style. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 86 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>{g.date}</Typography>
            <Box sx={{
              width: 10, height: 10, borderRadius: '50%', mt: 0.5,
              backgroundColor: theme.palette.primary.main,
            }} />
            <Box sx={{ flex: 1, width: 2, mt: 0.5, backgroundColor: alpha(theme.palette.primary.main, 0.2) }} />
          </Box>
          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0, pb: 1 }}>
            {g.entries.map((e, i) => (
              <HistoryEntry key={`${e.reportKey}-${e.version}-${i}`} entry={e} defaultOpen={filter !== 'all'} />
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );
};
